import React, { Children, cloneElement, Component } from 'react';
import { ChangeEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames, generateUUID, isFunction, isUndefined, shallowCompare, trigger } from '@philips-internal/oswald-ui-utils';
import { FormContext } from '../form/context/form-context.js';
import { Form } from '../form/form.js';
import { Link } from '../link/link.js';
import { FieldListProperties } from './field-list-types.js';
import './field-list.css';
const shallowCompareProps = [
    'children',
    'className',
    'enabled',
    'maxRows',
    'readOnly',
    'uuid',
    'value',
];
class FieldList extends Component {
    constructor() {
        super(...arguments);
        this.keys = [generateUUID()];
        this.onBlur = (event) => {
            const { onBlur } = this.props;
            if (!isFunction(onBlur)) {
                return;
            }
            onBlur(event);
        };
        this.onChange = (event, index) => {
            const { value = FieldListProperties.value.defaultValue } = this.props;
            const originCopy = [...value];
            originCopy.splice(index, 1, event.value);
            const newValue = originCopy;
            this.triggerOnChange({
                oldValue: value,
                value: newValue,
            });
        };
        this.onRemove = (index) => {
            const { value = FieldListProperties.value.defaultValue } = this.props;
            const newValue = [...value];
            newValue.splice(index, 1);
            this.keys.splice(index, 1);
            this.triggerOnChange({
                oldValue: value,
                value: newValue,
            });
        };
        this.onAddAnother = () => {
            const { value = FieldListProperties.value.defaultValue } = this.props;
            this.keys.push(generateUUID());
            this.triggerOnChange({
                oldValue: value,
                value: [...value, {}],
            });
        };
        this.getClassName = () => {
            const { className } = this.props;
            return classNames({
                'c-field-list': true,
                [`${className}`]: Boolean(className),
            });
        };
    }
    /**
     * Prettifies a given the labelKey of value.
     *
     * @param properties - Properties to generate pretty value.
     */
    static formatPrettyValue(properties) {
        const { labelKey = FieldListProperties.labelKey.defaultValue, previewAmount = FieldListProperties.previewAmount.defaultValue, value = [], } = properties;
        const valueIsArray = Array.isArray(value);
        const slicedValue = valueIsArray
            ? value.slice(0, previewAmount)
            : [];
        const item = !valueIsArray
            ? value[labelKey]
            : slicedValue.map(value => value[labelKey]).join(', ');
        if (!item) {
            return '';
        }
        const message = MessageService.translate(String(item));
        const moreValuesMessage = valueIsArray && value.length > previewAmount
            ? `+ ${MessageService.translate('tws.field-list.more', { quantity: value.length - slicedValue.length })}`
            : '';
        return `${message} ${moreValuesMessage}`.trim();
    }
    internalFormatPrettyValue(value) {
        const { labelKey = FieldListProperties.labelKey.defaultValue, previewAmount = FieldListProperties.previewAmount.defaultValue, } = this.props;
        return FieldList.formatPrettyValue({ labelKey, previewAmount, value });
    }
    shouldComponentUpdate(nextProps) {
        return shallowCompare(this.props, nextProps, shallowCompareProps);
    }
    triggerOnChange({ oldValue, value }) {
        const { onChange } = this.props;
        if (!isFunction(onChange)) {
            return;
        }
        const prettyValue = this.internalFormatPrettyValue(value);
        onChange(new ChangeEvent({ oldValue, prettyValue, value }));
    }
    renderRemove(index) {
        const { enabled = FieldListProperties.enabled.defaultValue, readOnly, removeFieldExpression = FieldListProperties.removeFieldExpression.defaultValue, showRemoveField = FieldListProperties.showRemoveField.defaultValue, value = FieldListProperties.value.defaultValue, } = this.props;
        if (!showRemoveField || value.length <= 1 || readOnly || !enabled) {
            return null;
        }
        return (React.createElement(Link, { className: "c-field-list__remove", onClick: trigger(this.onRemove, index), value: removeFieldExpression }));
    }
    renderRow(row, index) {
        const { children, enabled = FieldListProperties.enabled.defaultValue, readOnly, uuid = FieldListProperties.uuid.defaultValue, } = this.props;
        const formProps = {
            as: 'div',
            data: row,
            disabled: true,
            editing: !readOnly,
            onChange: (event) => this.onChange(event, index),
            uuid,
        };
        // Not really the best way to solve this, but is the only one that will
        // not cause new unnecessary renders.
        if (isUndefined(this.keys[index])) {
            this.keys[index] = generateUUID();
        }
        const childProps = {
            '@repeated': true,
            enabled,
            identifier: this.keys[index],
            onBlur: this.onBlur,
            readOnly,
        };
        return (React.createElement("div", { className: "c-field-list__row", "data-testid": "field-list-row", key: this.keys[index] },
            React.createElement(Form, { ...formProps }, Children.map(children, (child) => cloneElement(child, childProps))),
            this.renderRemove(index)));
    }
    renderAddAnother() {
        const { addFieldExpression = FieldListProperties.addFieldExpression.defaultValue, enabled = FieldListProperties.enabled.defaultValue, maxRows = FieldListProperties.maxRows.defaultValue, readOnly, showAddField = FieldListProperties.showAddField.defaultValue, value = FieldListProperties.value.defaultValue, } = this.props;
        if (!showAddField || readOnly || !enabled || value.length > maxRows) {
            return null;
        }
        return (React.createElement(Link, { className: "c-field-list__add", onClick: this.onAddAnother, value: addFieldExpression }));
    }
    render() {
        const { value = FieldListProperties.value.defaultValue } = this.props;
        return (React.createElement("div", { className: this.getClassName() },
            value.map((row, index) => this.renderRow(row, index)),
            this.renderAddAnother()));
    }
}
FieldList.contextType = FormContext;
export { FieldList, };
//# sourceMappingURL=field-list.js.map